// src/pages/NotificationsPage.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearError,
} from 'store/slices/notificationSlice';
import { Bell, Loader2, CheckCircle, Circle } from 'lucide-react';
// import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { ScrollArea } from 'components/ui/scroll-area';
import { toast } from 'sonner';
import PageHeader from 'components/common/PageHeader';
import { Button } from 'components/ui/button';

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const { list: notifications, unreadCount, pagination, isLoading, error } = useSelector(s => s.notification);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    dispatch(fetchNotifications({ page, pageSize }));
  }, [dispatch, page]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleMarkRead = async (id) => {
    await dispatch(markNotificationRead(id));
  };

  const handleMarkAllRead = async () => {
    await dispatch(markAllNotificationsRead());
    toast.success('All notifications marked as read');
  };

  const totalPages = Math.ceil(pagination.count / pagination.page_size);

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="All Notifications"
        description={`You have ${unreadCount} unread notifications`}
        // action={{
        //   label: 'Mark all as read',
        //   onClick: handleMarkAllRead,
        //   disabled: unreadCount === 0,
        // }}
      />

      {isLoading && !notifications.length ? (
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin mr-2" />
          <span className="text-lg">Loading notifications...</span>
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No notifications yet</p>
            <p className="text-sm text-muted-foreground">We'll notify you when something happens.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <ScrollArea className="h-[calc(100vh-260px)]">
            <div className="space-y-4 pr-4">
              {notifications.map((n) => (
                <Card
                  key={n?.id}
                  className={`cursor-pointer transition-colors hover:bg-muted/50 ${!n?.read ? 'border-primary/50' : ''}`}
                  onClick={() => !n?.read && handleMarkRead(n?.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {!n?.read ? (
                          <Circle className="h-4 w-4 fill-primary text-primary" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <CardTitle className="text-sm font-medium">
                          {n?.message}
                        </CardTitle>
                      </div>
                      {n?.type_display && (
                        <Badge variant="secondary" className="text-xs">
                          {n?.type_display}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground">
                      {new Date(n?.created_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({pagination.count} total)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;